(() => {
  const track = [0, 1, 2, 3, 4, 5, 6];
  const positionClasses = track.map((position) => `is-position-${position}`);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const LOSE_STEPS = [1, 2, 4];
  // priceName from openapi_v2 → board steps (legacy act_price_id: 1→317, 2→318, 3→319, 4→320)
  const PRIZE_STEPS = {
    1073: 6, // 1st
    1071: 3, // 2nd
    1070: 5, // 3rd
    1072: null, // no prize → random from LOSE_STEPS
  };
  const WIN_PRIZE_IDS = new Set([1073, 1071, 1070]);
  const FALLBACK_SKILL_BANK = [
    { ques: 1, numbers: [4, 2, 2, 1], operations: ["/", "+", "-"], answer: 3 },
    { ques: 2, numbers: [6, 2, 1, 1], operations: ["÷", "+", "-"], answer: 3 },
    { ques: 3, numbers: [8, 4, 3, 1], operations: ["÷", "+", "-"], answer: 4 },
    { ques: 4, numbers: [9, 3, 2, 1], operations: ["÷", "×", "-"], answer: 5 },
    { ques: 5, numbers: [5, 5, 2, 1], operations: ["+", "-", "×"], answer: 8 },
    { ques: 6, numbers: [7, 1, 2, 2], operations: ["+", "×", "-"], answer: 14 },
    { ques: 7, numbers: [10, 2, 3, 1], operations: ["÷", "+", "-"], answer: 7 },
    { ques: 8, numbers: [3, 3, 2, 1], operations: ["×", "+", "-"], answer: 10 },
  ];

  const DICE_ANIMATION_MS = 2000;
  const ACT_ID = "149";
  const MSG_ENTER_EMAIL = "Please enter your email first.";
  const MSG_EMAIL_LIMIT = "Email exceeded the limit for participation.";
  const MSG_IP_LIMIT = "The number of requests from the same IP address exceeded the limit.";
  const MSG_SERVER_ERROR = "Something went wrong. Please try again.";
  const COPY_BUBBLE_MS = 2000;

  const instances = new Set();

  const extractDiscountCode = (response) => {
    // Discount lives on response.data.code — do not use top-level `code` (API status).
    const code = response?.data?.code;
    if (code == null) return null;
    const trimmed = String(code).trim();
    return trimmed || null;
  };

  const getKlaviyoFormRoot = (root) =>
    root?.querySelector?.('[class^="klaviyo-form-"]') || null;

  const getKlaviyoFormId = (lottery, form) => {
    const fromData = lottery?.getAttribute?.("data-klaviyo-form-id");
    if (fromData) return fromData;

    const className = [...(form?.classList || [])].find((name) =>
      name.startsWith("klaviyo-form-")
    );
    return className ? className.slice("klaviyo-form-".length) : "";
  };

  const wait = (duration) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });

  const pickLoseSteps = () => {
    if (!window.crypto?.getRandomValues) {
      return LOSE_STEPS[Math.floor(Math.random() * LOSE_STEPS.length)];
    }

    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return LOSE_STEPS[values[0] % LOSE_STEPS.length];
  };

  const getStepsForPriceName = (priceName) => {
    const id = Number(priceName);
    if (id === 1072) return pickLoseSteps();
    return PRIZE_STEPS[id] || null;
  };

  const isWinPrizeId = (priceName) => WIN_PRIZE_IDS.has(Number(priceName));

  const getErrorMessage = (actStatus) => {
    if (actStatus === "no_finish") return MSG_EMAIL_LIMIT;
    if (actStatus === "no_finish_ip") return MSG_IP_LIMIT;
    return MSG_SERVER_ERROR;
  };

  const ajaxLottery = (email) => {
    return fetch("https://global.shokz.com/lottery/openapi_v2", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mbId: email,
        actId: ACT_ID,
        usageScenarios: "cs",
      }),
    }).then((res) => {
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      return res.json();
    });
  };

  const formatStatus = (template, steps) =>
    String(template || "").replaceAll("{steps}", String(steps));

  const getDiceTemplate = (lottery, steps) =>
    lottery.querySelector(`template[data-utmb26-lottery-dice="${steps}"]`);

  const showDiceForSteps = (lottery, slot, steps) => {
    if (!slot) return;

    const template = getDiceTemplate(lottery, steps);
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    slot.replaceChildren(fragment);
  };

  const parseSkillBank = (lottery) => {
    const node = lottery.querySelector("[data-utmb26-lottery-skill-test-bank]");
    if (!node) return FALLBACK_SKILL_BANK;

    try {
      const parsed = JSON.parse(node.textContent);
      if (!Array.isArray(parsed) || !parsed.length) return FALLBACK_SKILL_BANK;
      return parsed.filter(
        (item) =>
          Array.isArray(item?.numbers) &&
          item.numbers.length === 4 &&
          Array.isArray(item?.operations) &&
          item.operations.length === 3
      );
    } catch (error) {
      return FALLBACK_SKILL_BANK;
    }
  };

  const pickSkillQuestion = (bank) => {
    const list = bank?.length ? bank : FALLBACK_SKILL_BANK;
    if (!window.crypto?.getRandomValues) {
      return list[Math.floor(Math.random() * list.length)];
    }

    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return list[values[0] % list.length];
  };

  const formatSkillExpression = (question) => {
    const numbers = question?.numbers || [];
    const operations = question?.operations || [];
    if (numbers.length !== 4 || operations.length !== 3) return "";

    return `${numbers[0]}${operations[0]}${numbers[1]}${operations[1]}${numbers[2]}${operations[2]}${numbers[3]}=？`;
  };

  const parseModalConfig = (lottery) => {
    const node = lottery.querySelector("[data-utmb26-lottery-modal-config]");
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const setupModal = (lottery) => {
    const modal = lottery.querySelector("[data-utmb26-lottery-modal]");
    const dialog = lottery.querySelector("[data-utmb26-lottery-modal-dialog]");
    const closeButtons = lottery.querySelectorAll("[data-utmb26-lottery-modal-close]");
    const titleEl = lottery.querySelector("[data-utmb26-lottery-modal-title]");
    const subtitleEl = lottery.querySelector("[data-utmb26-lottery-modal-subtitle]");
    const heartEl = lottery.querySelector("[data-utmb26-lottery-modal-heart]");
    const imageEl = lottery.querySelector("[data-utmb26-lottery-modal-image]");
    const config = parseModalConfig(lottery);

    if (!modal || !dialog || !titleEl || !subtitleEl || !config) {
      return { open: () => { } };
    }

    let lastFocus = null;

    const close = () => {
      modal.classList.remove("is-open");
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("utmb26-lottery-modal-open");
      if (lastFocus && typeof lastFocus.focus === "function") {
        lastFocus.focus();
      }
    };

    const open = (position) => {
      const entry = config.positions?.[String(position)];
      if (!entry) return;

      const isWin = entry.type === "win";
      modal.dataset.variant = isWin ? "win" : "lose";

      if (isWin) {
        const prize = entry.prize || "Prize";
        const line2 = String(config.winTitleLine2 || " Le prix {prize} est à vous.").replaceAll(
          "{prize}",
          prize
        );
        titleEl.innerHTML = `${config.winTitleLine1 || "C'est gagné ! "}<br>${line2}`;
        subtitleEl.textContent = config.winSubtitle || "";
        if (heartEl) heartEl.hidden = true;
        if (imageEl) {
          imageEl.src = entry.image || imageEl.src;
          imageEl.alt = prize;
          imageEl.hidden = false;
          imageEl.style.display = "block";
        }
      } else {
        titleEl.textContent = config.loseTitle || "Vous étiez à deux doigts de gagner !";
        subtitleEl.innerHTML = config.loseSubtitle || "";
        if (imageEl) {
          imageEl.hidden = true;
          imageEl.removeAttribute("alt");
          imageEl.style.display = "none";
        }
        if (heartEl) heartEl.hidden = false;
      }

      lastFocus = document.activeElement;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("utmb26-lottery-modal-open");
      requestAnimationFrame(() => {
        modal.classList.add("is-open");
      });

      const closeBtn = modal.querySelector("[data-utmb26-lottery-modal-close]");
      closeBtn?.focus();
    };

    closeButtons.forEach((button) => {
      button.addEventListener("click", close);
    });

    modal.addEventListener("click", (event) => {
      if (event.target === modal) close();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) {
        close();
      }
    });

    return { open, close };
  };

  const setupNotice = (lottery, signal) => {
    const notice = lottery.querySelector("[data-utmb26-lottery-notice]");
    const textEl = lottery.querySelector("[data-utmb26-lottery-notice-text]");
    const closeButtons = lottery.querySelectorAll("[data-utmb26-lottery-notice-close]");

    if (!notice || !textEl) {
      return {
        open: () => { },
        close: () => { },
      };
    }

    let lastFocus = null;

    const close = () => {
      notice.classList.remove("is-open");
      notice.hidden = true;
      notice.setAttribute("aria-hidden", "true");
      document.body.classList.remove("utmb26-lottery-notice-open");
      if (lastFocus && typeof lastFocus.focus === "function") {
        lastFocus.focus();
      }
    };

    const open = (message) => {
      if (!message) return;
      textEl.textContent = message;
      lastFocus = document.activeElement;
      notice.hidden = false;
      notice.setAttribute("aria-hidden", "false");
      document.body.classList.add("utmb26-lottery-notice-open");
      requestAnimationFrame(() => {
        notice.classList.add("is-open");
      });
      closeButtons[0]?.focus();
    };

    closeButtons.forEach((button) => {
      button.addEventListener("click", close, { signal });
    });

    notice.addEventListener(
      "click",
      (event) => {
        if (event.target === notice) close();
      },
      { signal }
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape" && !notice.hidden) {
          close();
        }
      },
      { signal }
    );

    return { open, close };
  };

  const readEmailFromForm = (form) => {
    const input = form?.querySelector(
      'input[type="email"], input:not([type="checkbox"]):not([type="hidden"])'
    );
    return (input?.value || "").trim();
  };

  const setup = (lottery) => {
    if (lottery._utmb26Abort) {
      lottery._utmb26Abort.abort();
      instances.delete(lottery._utmb26Api);
    }

    const abort = new AbortController();
    const { signal } = abort;
    lottery._utmb26Abort = abort;

    const piece = lottery.querySelector("[data-utmb26-lottery-piece]");
    const button = lottery.querySelector("[data-utmb26-lottery-roll]");
    const status = lottery.querySelector("[data-utmb26-lottery-status]");
    const formWrap = lottery.querySelector("[data-utmb26-lottery-form]");
    const form = getKlaviyoFormRoot(lottery);
    const klaviyoFormId = getKlaviyoFormId(lottery, form);
    const postSubscribe = lottery.querySelector("[data-utmb26-lottery-post-subscribe]");
    const postWithCode = lottery.querySelector("[data-utmb26-lottery-post-with-code]");
    const postOffer = lottery.querySelector("[data-utmb26-lottery-post-offer]");
    const discountCodeEl = lottery.querySelector("[data-utmb26-lottery-discount-code]");
    const copyButton = lottery.querySelector("[data-utmb26-lottery-copy]");
    const copyBubble = lottery.querySelector("[data-utmb26-lottery-copy-bubble]");
    const consent = lottery.querySelector("[data-utmb26-lottery-consent]");
    const consentInput = lottery.querySelector("[data-utmb26-lottery-consent-input]");
    const consentError = lottery.querySelector("[data-utmb26-lottery-consent-error]");
    const skillTest = lottery.querySelector("[data-utmb26-lottery-skill-test]");
    const skillTestForm = lottery.querySelector("[data-utmb26-lottery-skill-test-form]");
    const skillTestQuestion = lottery.querySelector("[data-utmb26-lottery-skill-test-question]");
    const skillTestInput = lottery.querySelector("[data-utmb26-lottery-skill-test-input]");
    const diceSlot =
      lottery.querySelector("[data-utmb26-lottery-dice-slot]") ||
      button?.querySelector(".utmb26-lottery__dice") ||
      button;
    const skillBank = parseSkillBank(lottery);

    if (!piece || !button || !status) return;

    const modalApi = setupModal(lottery);
    const noticeApi = setupNotice(lottery, signal);

    let currentIndex = 0;
    let isMoving = false;
    let pendingSteps = null;
    let hasRolled = false;
    let cachedEmail = "";
    let isFetchingPrize = false;
    let hasHandledKlaviyoSuccess = false;
    let lastNoticeMessage = "";
    let currentDiscountCode = null;
    let copyBubbleTimer = 0;
    let pendingPrizePayload = null;
    let isAwaitingSkillTest = false;
    let currentSkillQuestion = null;

    const thanksMessage =
      lottery.getAttribute("data-notice-thanks") || "Thanks for your registration!";

    const setStatus = (text) => {
      status.textContent = text;
      status.classList.toggle("is-visible", Boolean(text));
    };

    const applyPostSubscribeContent = (code) => {
      currentDiscountCode = code;
      const hasCode = Boolean(code);

      if (postSubscribe) {
        postSubscribe.classList.toggle("is-offer-fallback", !hasCode);
      }
      if (postWithCode) {
        postWithCode.hidden = !hasCode;
      }
      if (postOffer) {
        postOffer.hidden = hasCode;
      }
      if (hasCode && discountCodeEl) {
        discountCodeEl.textContent = code;
      }
    };

    const setDiscountCode = (payloadOrCode) => {
      const code =
        typeof payloadOrCode === "string" || payloadOrCode == null
          ? payloadOrCode
          : extractDiscountCode(payloadOrCode);
      applyPostSubscribeContent(code);
    };

    const hideSkillTest = () => {
      isAwaitingSkillTest = false;
      currentSkillQuestion = null;
      if (formWrap) formWrap.classList.remove("is-skill-testing");
      if (skillTest) skillTest.hidden = true;
      if (skillTestInput) skillTestInput.value = "";
      if (skillTestQuestion) skillTestQuestion.textContent = "";
    };

    const showSkillTest = () => {
      if (!skillTest || !skillTestQuestion) return false;

      currentSkillQuestion = pickSkillQuestion(skillBank);
      const expression = formatSkillExpression(currentSkillQuestion);
      if (!expression) return false;

      isAwaitingSkillTest = true;
      hideConsent();
      if (formWrap) {
        formWrap.classList.remove("is-post-subscribe");
        formWrap.classList.add("is-skill-testing");
      }
      if (form) {
        form.hidden = true;
        form.setAttribute("aria-hidden", "true");
      }
      if (postSubscribe) postSubscribe.hidden = true;
      skillTestQuestion.textContent = expression;
      if (skillTestInput) skillTestInput.value = "";
      skillTest.hidden = false;
      skillTest.removeAttribute("hidden");
      requestAnimationFrame(() => skillTestInput?.focus());
      return true;
    };

    const hideConsent = () => {
      if (formWrap) {
        formWrap.classList.add("is-submitted");
        formWrap.classList.remove("is-consent-locked");
      }
      if (consent) {
        consent.hidden = true;
        consent.setAttribute("aria-hidden", "true");
      }
    };

    const isConsentChecked = () => Boolean(consentInput?.checked);

    const setConsentError = (show) => {
      if (consent) consent.classList.toggle("is-error", show);
      if (consentError) {
        consentError.hidden = !show;
        if (show) consentError.removeAttribute("hidden");
      }
    };

    const getKlaviyoSubmitButton = () =>
      form?.querySelector?.(
        '.needsclick[data-testid="form-row"] button, button[type="submit"], form button'
      ) || null;

    const syncConsentGate = () => {
      if (!consentInput || formWrap?.classList.contains("is-submitted")) return;

      const allowed = isConsentChecked();
      if (formWrap) formWrap.classList.toggle("is-consent-locked", !allowed);

      const submitBtn = getKlaviyoSubmitButton();
      if (submitBtn) {
        // Avoid native disabled — it swallows clicks and hides the consent error.
        submitBtn.removeAttribute("disabled");
        submitBtn.setAttribute("aria-disabled", String(!allowed));
      }

      if (allowed) setConsentError(false);
    };

    const blockSubmitWithoutConsent = (event) => {
      if (!consentInput || isConsentChecked() || formWrap?.classList.contains("is-submitted")) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const isSubmitEvent = event.type === "submit";
      const isSubmitClick =
        event.type === "click" && Boolean(target.closest("button"));
      const isSubmitKey =
        event.type === "keydown" &&
        event.key === "Enter" &&
        Boolean(
          target.closest(
            'input:not([type="checkbox"]):not([type="hidden"]), textarea, button'
          )
        );

      if (!isSubmitEvent && !isSubmitClick && !isSubmitKey) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setConsentError(true);
      consentInput.focus?.();
    };

    if (consentInput) {
      syncConsentGate();
      consentInput.addEventListener(
        "change",
        () => {
          syncConsentGate();
        },
        { signal }
      );

      if (form) {
        form.addEventListener("click", blockSubmitWithoutConsent, {
          signal,
          capture: true,
        });
        form.addEventListener("submit", blockSubmitWithoutConsent, {
          signal,
          capture: true,
        });
        form.addEventListener("keydown", blockSubmitWithoutConsent, {
          signal,
          capture: true,
        });

        const consentObserver = new MutationObserver(() => {
          syncConsentGate();
        });
        consentObserver.observe(form, { childList: true, subtree: true });
        signal.addEventListener("abort", () => consentObserver.disconnect());
      }
    }

    const showPostSubscribe = () => {
      hideSkillTest();
      hideConsent();
      if (formWrap) formWrap.classList.add("is-post-subscribe");
      if (form) {
        form.hidden = true;
        form.setAttribute("aria-hidden", "true");
      }
      if (postSubscribe) {
        postSubscribe.hidden = false;
        postSubscribe.removeAttribute("hidden");
      }
    };

    const hidePostSubscribe = () => {
      if (formWrap) formWrap.classList.remove("is-post-subscribe");
      if (form) {
        form.hidden = false;
        form.removeAttribute("aria-hidden");
      }
      if (postSubscribe) {
        postSubscribe.hidden = true;
      }
    };

    const continueAfterPrizeReady = async (data, steps) => {
      if(data.data.discountCode) {
        setDiscountCode(data);
        showPostSubscribe();
      }
      else {
      //  keepKlaviyoThanks();
      }

      pendingSteps = steps;
      pendingPrizePayload = null;
      setStatus(lottery.dataset.idleText || "Ready to roll");
      await movePiece();
    };

    const openNotice = (message) => {
      lastNoticeMessage = message;
      noticeApi.open(message);
    };

    const cacheEmail = (email) => {
      const next = (email || readEmailFromForm(form) || "").trim();
      if (next) cachedEmail = next;
      return cachedEmail;
    };

    const hideCopyBubble = () => {
      if (copyBubbleTimer) {
        window.clearTimeout(copyBubbleTimer);
        copyBubbleTimer = 0;
      }
      if (copyBubble) copyBubble.hidden = true;
    };

    const showCopyBubble = () => {
      if (!copyBubble) return;
      copyBubble.hidden = false;
      if (copyBubbleTimer) window.clearTimeout(copyBubbleTimer);
      copyBubbleTimer = window.setTimeout(hideCopyBubble, COPY_BUBBLE_MS);
    };

    const copyDiscountCode = async () => {
      if (!currentDiscountCode) return;
      const code = currentDiscountCode;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement("textarea");
          input.value = code;
          input.setAttribute("readonly", "");
          input.style.position = "absolute";
          input.style.left = "-9999px";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        showCopyBubble();
      } catch (error) {
        console.error("UTMB26 lottery copy error:", error);
      }
    };

    if (copyButton) {
      copyButton.addEventListener("click", copyDiscountCode, { signal });
    }
    signal.addEventListener("abort", hideCopyBubble);

    const completeSkillTest = async () => {
      if (!isAwaitingSkillTest || !pendingPrizePayload) return;

      const { data, steps } = pendingPrizePayload;
      // Answer correctness is intentionally ignored: no feedback, always continue.
      hideSkillTest();
      await continueAfterPrizeReady(data, steps);
    };

    if (skillTestForm) {
      skillTestForm.addEventListener(
        "submit",
        (event) => {
          event.preventDefault();
          completeSkillTest();
        },
        { signal }
      );
    }

    const requestPrizeForEmail = async (emailFromEvent) => {
      if (isFetchingPrize || pendingSteps || hasRolled || isAwaitingSkillTest) return;

      const email = cacheEmail(emailFromEvent);
      if (!email) {
        setStatus(MSG_ENTER_EMAIL);
        return;
      }

      // Hide consent as soon as Klaviyo submit succeeds / lottery starts.
      hideConsent();

      isFetchingPrize = true;
      let shouldAutoRoll = false;
      let autoRollData = null;
      let autoRollSteps = null;

      const keepKlaviyoThanks = (statusText) => {
        hideSkillTest();
        hidePostSubscribe();
        hideConsent();
        if (statusText) setStatus(statusText);
      };

      try {
        const data = await ajaxLottery(email);
        const payload = data?.data || {};
        const actStatus = payload.status ?? payload.act_status;

        // IP limit: keep Klaviyo thank-you UI, hide consent, and show IP limit notice.
        if (actStatus === "no_finish_ip") {
          keepKlaviyoThanks(MSG_IP_LIMIT);
          openNotice(MSG_IP_LIMIT);
        } else if (actStatus === "no_finish") {
          keepKlaviyoThanks(MSG_EMAIL_LIMIT);
          openNotice(MSG_EMAIL_LIMIT);
        } else if (actStatus === "ok") {
          const prizeId = payload.priceId;
          const steps = getStepsForPriceName(prizeId);
          const code = extractDiscountCode(data);
          // if (!steps || !code) {
          //   keepKlaviyoThanks(MSG_SERVER_ERROR);
          // } else 
            if (isWinPrizeId(prizeId) && skillTest) {
            // 1st / 2nd / 3rd: gate prize UI behind skill-testing question.
            pendingPrizePayload = { data, steps };
            if (!showSkillTest()) {
              pendingPrizePayload = null;
              autoRollData = data;
              autoRollSteps = steps;
              shouldAutoRoll = true;
            }
          } else {
            autoRollData = data;
            autoRollSteps = steps;
            shouldAutoRoll = true;
          }
        } else {
          const errorMessage = getErrorMessage(actStatus);
          if (errorMessage === MSG_SERVER_ERROR) {
            keepKlaviyoThanks(MSG_SERVER_ERROR);
          } else {
            setDiscountCode(data);
            showPostSubscribe();
            openNotice(errorMessage);
            setStatus(errorMessage);
          }
        }
      } catch (error) {
        console.error("UTMB26 lottery error:", error);
        keepKlaviyoThanks(MSG_SERVER_ERROR);
      } finally {
        isFetchingPrize = false;
      }

      if (shouldAutoRoll && autoRollData && autoRollSteps) {
        await continueAfterPrizeReady(autoRollData, autoRollSteps);
      }
    };

    const markKlaviyoSuccessUi = () => {
      if (hasHandledKlaviyoSuccess) return true;

      if (!form) {
        hasHandledKlaviyoSuccess = true;
        return true;
      }

      const thanks = form.querySelector("h1 span, h1");
      if (!thanks) return false;

      const text = thanks.textContent?.trim() || "";
      if (!/thanks for your registration/i.test(text)) {
        return false;
      }

      hasHandledKlaviyoSuccess = true;
      return true;
    };

    if (form) {
      form.addEventListener("input", () => cacheEmail(), { signal, capture: true });
      form.addEventListener("change", () => cacheEmail(), { signal, capture: true });
      form.addEventListener(
        "pointerdown",
        (event) => {
          if (event.target.closest("button")) cacheEmail();
        },
        { signal, capture: true }
      );

      if (!markKlaviyoSuccessUi()) {
        const observer = new MutationObserver(() => {
          if (markKlaviyoSuccessUi()) observer.disconnect();
        });
        observer.observe(form, { childList: true, subtree: true, characterData: true });
        signal.addEventListener("abort", () => observer.disconnect());
      }
    }

    const heading = lottery.querySelector(".utmb26-lottery__heading");

    const shakeHeading = () => {
      if (!heading || reduceMotion.matches) return;
      heading.classList.remove("is-shake");
      void heading.offsetWidth;
      heading.classList.add("is-shake");
    };

    if (heading) {
      heading.addEventListener(
        "animationend",
        () => {
          heading.classList.remove("is-shake");
        },
        { signal }
      );
    }

    const moveOneSpace = async () => {
      currentIndex = (currentIndex + 1) % track.length;
      const nextPosition = track[currentIndex];

      piece.classList.remove(...positionClasses, "is-stepping");
      lottery.classList.remove(...positionClasses, "is-stepping");
      piece.classList.add(`is-position-${nextPosition}`);
      lottery.classList.add(`is-position-${nextPosition}`);

      if (!reduceMotion.matches) {
        void piece.offsetWidth;
        piece.classList.add("is-stepping");
        await wait(430);
      }
    };

    const movePiece = async () => {
      if (isMoving) return;

      if (hasRolled || (!pendingSteps && lastNoticeMessage)) {
        if (lastNoticeMessage) noticeApi.open(lastNoticeMessage);
        return;
      }

      if (!pendingSteps) {
        setStatus(MSG_ENTER_EMAIL);
        shakeHeading();
        return;
      }

      const steps = pendingSteps;
      pendingSteps = null;
      isMoving = true;
      hasRolled = true;
      button.disabled = true;
      noticeApi.close();
      lottery.setAttribute("aria-busy", "true");

      showDiceForSteps(lottery, diceSlot, steps);
      setStatus(formatStatus(lottery.dataset.movingText, steps));

      if (!reduceMotion.matches) {
        await wait(DICE_ANIMATION_MS);
      }

      for (let step = 0; step < steps; step += 1) {
        await moveOneSpace();
      }

      piece.classList.remove("is-stepping");
      setStatus(formatStatus(lottery.dataset.resultText, steps));
      lottery.removeAttribute("aria-busy");
      isMoving = false;
      button.disabled = false;

      const finalPosition = track[currentIndex];
      if (finalPosition > 0) {
        modalApi.open(finalPosition);
      }
    };

    const api = {
      lottery,
      formId: klaviyoFormId,
      handleEmailSubmit: (email) => {
        hasHandledKlaviyoSuccess = true;
        return requestPrizeForEmail(email);
      },
    };

    lottery._utmb26Api = api;
    instances.add(api);

    button.addEventListener("click", movePiece, { signal });
    lottery.dataset.utmb26LotteryInitialized = "true";
  };

  const init = (root = document) => {
    if (root.matches?.("[data-utmb26-lottery]")) {
      setup(root);
    }

    root.querySelectorAll?.("[data-utmb26-lottery]").forEach(setup);
  };

  if (!window.__utmb26KlaviyoFormsBound) {
    window.__utmb26KlaviyoFormsBound = true;
    window.addEventListener("klaviyoForms", (event) => {
      const detail = event.detail || {};
      if (detail.type !== "submit") return;

      const email = detail.metaData?.$email || "";
      instances.forEach((api) => {
        if (!api?.lottery || !document.contains(api.lottery)) {
          instances.delete(api);
          return;
        }
        if (detail.formId && api.formId && detail.formId !== api.formId) return;
        api.handleEmailSubmit(email);
      });
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  window.utmb26Lottery = { init };
  init();
})();
